/* eslint-disable @typescript-eslint/no-magic-numbers */
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import useDismissableDialog from 'hooks/util/useDismissableDialog';

export type DialogBoxContextType = {
  showDialogBox: (message: string, onClose?: () => void) => void;
};

const DialogBoxContext = createContext<DialogBoxContextType | undefined>(undefined);

export const useDialogBox = () => {
  const context = useContext(DialogBoxContext);
  if (!context) {
    throw new Error('Dialog must be used within a DialogBoxProvider');
  }
  return context;
};

export function DialogBoxProvider({ children }: { children: ReactNode }) {
  const [dialogBox, setDialogBox] = useState<{
    message: string;
    open: boolean;
    onClose?: () => void;
  }>({
    message: '',
    open: false,
    onClose: () => {},
  });

  const onClose = useCallback(() => {
    setDialogBox({ message: '', open: false });
    if (dialogBox.onClose) dialogBox.onClose();
  }, [dialogBox]);

  const { dialogReference, onDismiss } = useDismissableDialog({
    isOpen: dialogBox.open,
    onClose,
    dismissable: true,
  });

  const showDialogBox = useCallback((message: string, onCloseCallback?: () => void) => {
    setDialogBox({
      message,
      open: true,
      onClose: onCloseCallback,
    });
  }, []);

  const value = useMemo(() => ({ showDialogBox }), [showDialogBox]);

  return (
    <DialogBoxContext.Provider value={value}>
      {children}
      {dialogBox.open && dialogBox.message && (
        <div
          className="fixed inset-0 z-10 flex h-screen w-screen items-center justify-center"
          style={{
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 47, 118, 0.85)',
          }}
        >
          <dialog
            open
            ref={dialogReference}
            className="relative flex min-h-[350px] w-[90%] max-w-[500px] flex-col items-center justify-center rounded-[16px] bg-white p-6 text-center shadow-lg"
          >
            <p className="mb-4 text-3xl font-semibold text-primary-blue-3">{dialogBox.message}</p>
            {/* <p className="mb-8 text-lg text-gray-600">{dialogBox.message}</p> */}
            <button
              type="button"
              onClick={onDismiss}
              className="w-1/2 rounded-full bg-primary-yellow-3 px-6 py-3 text-lg font-semibold text-primary-blue-3 shadow-md transition-all duration-300 hover:bg-primary-yellow-4"
            >
              Okay!
            </button>
          </dialog>
        </div>
      )}
    </DialogBoxContext.Provider>
  );
}
